import { AxiosError } from "axios";
import { AnsweringClient } from "../clients/answeringClient";

export interface IAnswer {
  description: string;
  order: number;
  selected: boolean;
  text: string;
  icon: string;
  allow_audio: boolean;
  icon_size: string;
}

export interface IQuestion {
  answers: IAnswer[];
  description: string;
  order: number;
  pattern: string;
  title: string;
  type: string;
  audio: boolean;
  text_to_audio: string;
  question_config?: Record<string, unknown>;
}

export interface QuestionsControllerDelegate {
  genericError(message?: string): void;
  didGetQuestion(question: IQuestion): void;
  didSaveAnswer(): void;
  didEndInstrument(): void;
  applicationShouldLogout(): void;
  /**
   * The answer was rejected because the assignment has no open answer slot
   * (backend 409). Re-fetching the question opens a fresh slot, so the screen
   * should reload it and let the user submit again.
   */
  shouldReloadQuestion(): void;
  /**
   * The answer is already recorded server-side (the "already answered"
   * domain 403 of a non-backtracking instrument). The screen should advance:
   * re-fetching returns the next unanswered question, never the answered one.
   */
  questionAlreadyAnswered(): void;
}

export class QuestionsController {
  constructor(
    private delegate: QuestionsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getNextQuestion(
    authorizationToken: string,
    assignmentToken: string,
    type: string,
    hasRetriedAfterRefresh = false
  ) {
    try {
      let response;
      if (type === "linear") {
        response = await this.answeringClient.getNextQuestion(
          authorizationToken,
          assignmentToken,
          "next"
        );
      } else {
        response = await this.answeringClient.getNextQuestion(
          authorizationToken,
          assignmentToken,
          type
        );
      }
      this.delegate.didGetQuestion(response.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        const response = error.response;
        if (response?.status === 400 && response?.data?.code === 20001) {
          this.delegate.didEndInstrument();
          return;
        }

        // One refresh retry only: the route is also fronted by the
        // instrument-token authenticator, whose 403 (an invalid instrument
        // token) a token refresh cannot clear — retrying forever would loop.
        if (response?.status === 403 && !hasRetriedAfterRefresh) {
          const newTokens = await this.refreshTokens();
          if (!newTokens) {
            return;
          }
          await this.getNextQuestion(
            newTokens.accessToken,
            assignmentToken,
            type,
            true
          );
          return;
        }
      }
      this.delegate.genericError(this.extractErrorMessage(error));
    }
  }

  /**
   * 403s carry no machine-readable code, so the body text is the only way to
   * tell "already answered" (the answer IS saved — safe to advance) from
   * other persistent 403s such as an invalid Instrument-Token. Unknown 403s
   * must fail safe to a terminal error, never to a false "already recorded"
   * recovery.
   */
  private isAlreadyAnsweredError(error: any): boolean {
    return /already answered/i.test(error?.response?.data?.message ?? "");
  }

  public async sendAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    answerOrders: number[],
    hasRetriedAfterRefresh = false
  ) {
    try {
      await this.answeringClient.sendAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        answerOrders
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      // The answer routes also emit domain 403s (e.g. question already
      // answered on a non-backtracking instrument), so a 403 that survives one
      // token refresh is not expiry — retrying further would loop forever.
      if (error?.response?.status === 403 && !hasRetriedAfterRefresh) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        await this.sendAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          answerOrders,
          true
        );
        return;
      }
      this.handleSaveError(error);
    }
  }

  /**
   * Terminal handling for a failed save, shared by all four answer routes.
   * The backend gives them one contract, so branching on status in four places
   * only creates room to drift — which is how the audio route came to
   * dead-end on a 409 that the click route recovered from.
   */
  private handleSaveError(error: any): void {
    const status = error?.response?.status;
    // Only reachable post-refresh. When the 403 is "already answered" the
    // answer IS saved — advance the user instead of dead-ending on an
    // answered question; any other persistent 403 is a terminal error.
    if (status === 403) {
      if (this.isAlreadyAnsweredError(error)) {
        this.delegate.questionAlreadyAnswered();
      } else {
        this.delegate.genericError(this.extractErrorMessage(error));
      }
      return;
    }
    if (status === 400 && error?.response?.data?.code === 20001) {
      this.delegate.didEndInstrument();
      return;
    }
    // 409 = no open answer slot for this assignment (genuine desync);
    // a re-fetch of the question opens a fresh slot.
    if (status === 409) {
      this.delegate.shouldReloadQuestion();
      return;
    }
    this.delegate.genericError(this.extractErrorMessage(error));
  }

  public async sendFreeTextAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    answer: string,
    hasRetriedAfterRefresh = false
  ) {
    try {
      await this.answeringClient.sendFreeTextAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        answer
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      // See sendAnswer: one refresh retry only — a persisting 403 is a domain
      // error, not token expiry.
      if (error?.response?.status === 403 && !hasRetriedAfterRefresh) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        await this.sendFreeTextAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          answer,
          true
        );
        return;
      }
      this.handleSaveError(error);
    }
  }

  public async sendTaskAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    taskAnswer: object,
    hasRetriedAfterRefresh = false
  ) {
    try {
      await this.answeringClient.sendTaskAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        taskAnswer
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      // See sendAnswer: one refresh retry only — a persisting 403 is a domain
      // error, not token expiry.
      if (error?.response?.status === 403 && !hasRetriedAfterRefresh) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        await this.sendTaskAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          taskAnswer,
          true
        );
        return;
      }
      this.handleSaveError(error);
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }

  public async sendAudioAnswer(
    authorizationToken: string,
    assignmentToken: string,
    questionOrder: number,
    audioBlob: Blob,
    hasRetriedAfterRefresh = false
  ) {
    try {
      await this.answeringClient.sendAudioAnswer(
        authorizationToken,
        assignmentToken,
        questionOrder,
        audioBlob
      );
      this.delegate.didSaveAnswer();
    } catch (error: any) {
      // See sendAnswer: one refresh retry only — a persisting 403 is a domain
      // error, not token expiry.
      if (error?.response?.status === 403 && !hasRetriedAfterRefresh) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        await this.sendAudioAnswer(
          newTokens.accessToken,
          assignmentToken,
          questionOrder,
          audioBlob,
          true
        );
        return;
      }
      this.handleSaveError(error);
    }
  }

  private extractErrorMessage(error: any): string | undefined {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
    );
  }
}
